// Site-placement rules. Pure. No React, no Three.js. Decides
// which cells a player can place a Site at, and whether the player
// is in the codex "mandatory site under Avatar" state.
//
// ── Codex anchors ──
//
//   Casting Sites. A site placed from hand goes onto a cell
//     adjacent (cardinal) to a site you control. The first site
//     of the game goes under your Avatar (which starts in the void).
//   Avatar in Void. "If your Avatar is in the void, you may and
//     must use their ability to play a site under them, ignoring
//     normal site adjacency rules."
//   Mandatory Actions. If you can perform the mandatory action,
//     you MUST do it before other actions in your main phase.
//
// ── Returned shape ──
//
//   {
//     underAvatar:  { col, row } | null   the avatar's current cell,
//                                          always a legal placement
//                                          for the controller's site
//     adjacent:     Set<'col,row'>         cells cardinal-adjacent to a
//                                          site the controller already
//                                          controls (Rubble excluded
// It doesn't count as a
//                                          source site for adjacency)
//     mandatory:    boolean                avatar is in void + the
//                                          player MUST place under it
//                                          before other actions
//   }

import { regionsAtCell } from './regions';

const RUBBLE_ID = 'sorcery-rubble';

const CARDINAL_DELTAS = [[1, 0], [-1, 0], [0, 1], [0, -1]];

function findAvatarCell(controllerRotated, meshes, cellAt) {
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || card.type !== 'Avatar') continue;
    if (!!card.rotated !== !!controllerRotated) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (cell) return cell;
  }
  return null;
}

// True iff cell (col, row) is a legal site-placement target. I.e. it
// holds no non-Rubble Site. Used to prune the adjacency placement set.
// Per codex a site goes on a void (empty) cell and you cannot place
// over your opponent's or your own existing site, BUT Rubble is
// replaceable: "this can happen if Rubble was replaced by a site"
// (codex "Enter"), so a Rubble-only cell counts as placeable.
function cellIsVoid(meshes, cellAt, col, row) {
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card?.isSite) continue;
    if (card.cardId === RUBBLE_ID) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    if (cell.col === col && cell.row === row) return false;
  }
  return true;
}

/**
 * True iff the controller's Avatar is currently in the void (i.e.
 * the avatar is on a cell with no site beneath it). This is the
 * trigger for the codex "may and must play a site under them"
 * mandatory action. Caller is responsible for the additional
 * "has a site in hand" check.
 */
export function isAvatarInVoid(controllerRotated, meshes, cellAt, getFullCard) {
  const cell = findAvatarCell(controllerRotated, meshes, cellAt);
  if (!cell) return false;
  const regions = regionsAtCell(meshes, cellAt, cell.col, cell.row, getFullCard);
  // "Void" is the absence-of-site marker per regions.js. If the
  // cell also has a surface (some site IS there), it's not void.
  return regions.has('void') && !regions.has('surface');
}

/**
 * Full legal-placement summary for a Site this controller wants to
 * play. Combines the avatar-anchor cell, the adjacency neighbours
 * of every non-Rubble controlled site, and the mandatory flag.
 *
 * @param {Set<string>} [bannedCells]. `'col,row'` keys that are
 *   forbidden for any incoming site (Salt the Earth's covered cells).
 *   Filters both `adjacent` and the mandatory `underAvatar` entry.
 *   if the avatar is in void on a banned cell, no placement is legal
 *   and `mandatory` flips off (no site can resolve the void state).
 */
export function getLegalSitePlacement(controllerRotated, meshes, cellAt, getFullCard, totalCols, totalRows, bannedCells = null) {
  const avatarCell = findAvatarCell(controllerRotated, meshes, cellAt);
  const isBanned = (col, row) => bannedCells && bannedCells.has(`${col},${row}`);

  // First gather the raw cardinal neighbours of every controlled
  // non-Rubble site (geometric set), then filter to cells that are
  // actually playable. I.e. void (no site of any kind). Without the
  // void filter the highlight would advertise the cell next to your
  // site even when your OPPONENT already occupies it, which the codex
  // forbids ("a site placed from hand goes onto a [void] cell adjacent
  // to a site you control").
  const candidate = new Set();
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card?.isSite) continue;
    if (card.cardId === RUBBLE_ID) continue;
    if (!!card.rotated !== !!controllerRotated) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    for (const [dc, dr] of CARDINAL_DELTAS) {
      const nc = cell.col + dc;
      const nr = cell.row + dr;
      if (totalCols != null && (nc < 0 || nc >= totalCols)) continue;
      if (totalRows != null && (nr < 0 || nr >= totalRows)) continue;
      candidate.add(`${nc},${nr}`);
    }
  }
  const adjacent = new Set();
  for (const key of candidate) {
    const [colStr, rowStr] = key.split(',');
    const col = parseInt(colStr, 10);
    const row = parseInt(rowStr, 10);
    if (isBanned(col, row)) continue;
    if (cellIsVoid(meshes, cellAt, col, row)) adjacent.add(key);
  }

  let mandatory = false;
  let underAvatar = avatarCell;
  if (avatarCell) {
    const regions = regionsAtCell(meshes, cellAt, avatarCell.col, avatarCell.row, getFullCard);
    mandatory = regions.has('void') && !regions.has('surface');
    // Salt-the-Earth-style ban also disqualifies the under-avatar
    // mandatory cell. No site can resolve the void state, so the
    // mandatory action is suppressed. Caller treats it as "no legal
    // placement available."
    if (isBanned(avatarCell.col, avatarCell.row)) {
      underAvatar = null;
      mandatory = false;
    }
  }

  return {
    underAvatar,
    adjacent,
    mandatory,
  };
}

/**
 * Convenience: given the legal-placement summary and a target cell,
 * classify the drop. Returns one of:
 *   'under_avatar' | 'adjacent' | 'illegal'
 * The orchestrator combines this with the grant-state to decide
 * whether to commit.
 */
export function classifyCellForSite(legal, toCol, toRow) {
  if (!legal) return 'illegal';
  if (legal.underAvatar && legal.underAvatar.col === toCol && legal.underAvatar.row === toRow) {
    return 'under_avatar';
  }
  if (legal.adjacent.has(`${toCol},${toRow}`)) return 'adjacent';
  return 'illegal';
}

/**
 * Owner-relative snap point for a site card in a cell.
 *
 * Sites are visually anchored at the controller's near edge of the cell
 * (bottom-center from THEIR perspective): P1 sees their sites against
 * the world-bottom (bl/br) edge of each cell, P2 sees their sites
 * against the world-top (tl/tr) edge. Which appears at the bottom of
 * P2's flipped camera view.
 *
 * The snap point is one-quarter of the cell depth in from the near
 * edge so the card body reads "anchored to the near edge" without
 * extending past the cell when ported across grid scales. Pure. Takes
 * the cell's four corners (from `sampleCellGeometry` or `getCellAt`)
 * and the controller's `rotated` flag, returns world `{x, z}`.
 */
export function siteSnapPoint(geo, controllerRotated) {
  if (!geo || !geo.tl || !geo.tr || !geo.bl || !geo.br) return null;
  const nearX = controllerRotated ? (geo.tl.x + geo.tr.x) / 2 : (geo.bl.x + geo.br.x) / 2;
  const nearZ = controllerRotated ? (geo.tl.z + geo.tr.z) / 2 : (geo.bl.z + geo.br.z) / 2;
  const farX  = controllerRotated ? (geo.bl.x + geo.br.x) / 2 : (geo.tl.x + geo.tr.x) / 2;
  const farZ  = controllerRotated ? (geo.bl.z + geo.br.z) / 2 : (geo.tl.z + geo.tr.z) / 2;
  const t = 0.25;
  return {
    x: nearX + (farX - nearX) * t,
    z: nearZ + (farZ - nearZ) * t,
  };
}
